import { useTranslation } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { ButtonLink, RenderDataWithFallback } from '@anytag/shared-atoms';
import { Header, ListWithPagination } from '@anytag/shared-molecules';
import { getOffset, getValuableVariables } from '@anytag/shared-utils';
import { useUpdateDocumentTitle } from '@anytag/shared-hooks';
import { TiktokBusinessWarningStatus, useAllTiktokSpecialCampaignsQuery } from '@anytag/graphql';
import {
  TikTokCampaignsList,
  TikTokCampaignsFilter,
  useTikTokCampaignsFilter,
  TikTokSummaryDataContext,
  TikTokCampaignsPlansOnboarding
} from '@anytag/shared-organisms';
import { CreateCampaignFailedModal } from './create-campaign-failed-modal';

export const TikTokCampaigns = ({ summaryData }: Pick<TikTokSummaryDataContext, 'summaryData'>) => {
  useUpdateDocumentTitle({
    title: 'documentTitle.TikTok Campaigns',
    appHeader: 'appHeader.TikTok Campaigns'
  });
  const { t } = useTranslation();
  const { isAdvertiserFromTikTok } = useUserRoles();
  const { page, limit, filter } = useTikTokCampaignsFilter();

  const needToSubscribeMkpPlan = summaryData?.warningsList.includes(TiktokBusinessWarningStatus.MKP_CAMPAIGN_DISABLE);

  const { data, previousData, loading, refetch } = useAllTiktokSpecialCampaignsQuery({
    skip: needToSubscribeMkpPlan,
    variables: getValuableVariables({
      limit,
      offset: getOffset(page, limit),
      ...filter,
      status: filter.status || null
    })
  });

  const result = loading ? previousData : data;
  const totalRecords = result?.allTiktokSpecialCampaignsCount?.totalNumber;

  return (
    <RenderDataWithFallback loading={loading}>
      {needToSubscribeMkpPlan ? (
        <TikTokCampaignsPlansOnboarding />
      ) : (
        <>
          <Header>
            {isAdvertiserFromTikTok && (
              <ButtonLink
                variant="solid"
                className="ml-auto min-w-[143px]"
                to="/tiktok/campaigns/add"
                title={t('Button.Add Campaign')}
                prefixIcon={{ icon: 'plus', size: '10px' }}
              />
            )}
          </Header>

          <ListWithPagination pagination={{ totalRecords }}>
            <TikTokCampaignsFilter />
            <TikTokCampaignsList loading={loading} data={result?.allTiktokSpecialCampaigns || []} />
          </ListWithPagination>

          <CreateCampaignFailedModal onRefetchCampaignsList={refetch} />
        </>
      )}
    </RenderDataWithFallback>
  );
};
